import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Clock, FileText, UserPlus, HeartHandshake, MapPin, Phone, ChevronRight, ArrowRight, Church, Camera, Loader2, Sparkles, HandHeart, Calendar, Newspaper, Feather } from 'lucide-react';
import Hero from '../components/Hero';
import { QUICK_ACTIONS, SACRAMENTS, MISSION_VERSE } from '../data/mock';
import { api } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../hooks/use-toast';
import { AddButton } from '../components/AdminControls';
import EditDialog from '../components/EditDialog';
import ConfirmDialog from '../components/ConfirmDialog';
import EditableText from '../components/EditableText';
import EditableInline from '../components/EditableInline';
import NewsCard from '../components/NewsCard';
import { TileCard } from '../components/TileSection';

const iconMap = { Clock, FileText, UserPlus, HeartHandshake, Sparkles };

// Compact Parish Life tiles shown in the Home "Get Involved" section.
// The Overview page (/parish-life) is reachable through the "All parish life" link.
const PARISH_LIFE_TILES = [
  {
    title: "I'm New",
    caption: "Just arrived or exploring the Catholic Faith? Start here.",
    icon: Sparkles,
    path: '/parish-life/im-new',
  },
  {
    title: 'Ministries',
    caption: "Serve and grow — liturgical, community, faith formation, outreach.",
    icon: HandHeart,
    path: '/parish-life/ministries',
  },
  {
    title: 'Parish Calendar',
    caption: "Masses, sacraments, meetings, socials, and special liturgies.",
    icon: Calendar,
    path: '/parish-life/calendar',
  },
  {
    title: 'News & Resources',
    caption: "The latest parish news and trusted Catholic resources.",
    icon: Newspaper,
    path: '/parish-life/news',
  },
  {
    title: 'Funeral Planning',
    caption: "Catholic funeral rites and support for your family, step by step.",
    icon: Feather,
    path: '/parish-life/funeral',
  },
];

const NEWS_FIELDS = [
  { name: 'date', label: 'Date / Label', required: true, placeholder: 'This Sunday' },
  { name: 'title', label: 'Title', required: true },
  {
    name: 'excerpt', label: 'Excerpt', type: 'textarea', required: true, rows: 4,
    help: 'Any URL you paste (e.g. https://example.com) or email address will automatically become a clickable link on the site.',
  },
];

const Divider = () => (
  <div className="divider-cross my-2">
    <Church size={18} strokeWidth={1.4} />
  </div>
);

const Home = () => {
  const { isAdmin } = useAuth();
  const { toast } = useToast();
  const [churches, setChurches] = useState([]);
  const [news, setNews] = useState([]);
  const [featuredNews, setFeaturedNews] = useState([]);
  const [featuredTiles, setFeaturedTiles] = useState([]);
  const [editing, setEditing] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  // Church image state — { 'st-marys': { exists, updatedAt }, ... }
  const [churchImgs, setChurchImgs] = useState({});
  const [churchImgsLoaded, setChurchImgsLoaded] = useState(false);
  const [uploadingChurch, setUploadingChurch] = useState(null);
  const fileInputRefs = useRef({});
  // News photo upload state (id currently uploading)
  const [uploadingNewsId, setUploadingNewsId] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const [sch, nws, fn, ft] = await Promise.all([
          api.listSchedules(), api.listNews(), api.listFeaturedNews(), api.listHomeFeaturedTiles(),
        ]);
        setChurches(sch.data);
        setNews(nws.data);
        setFeaturedNews(fn.data);
        setFeaturedTiles(ft.data);
      } catch { /* silent */ }
    };
    load();
  }, []);

  // Fetch church image metadata to know whether to show the uploaded photo
  // or fall back to the built-in stock image. Until this resolves we render
  // a neutral placeholder so the fallback stock image never flashes before
  // an admin-uploaded photo.
  useEffect(() => {
    const loadImgs = async () => {
      const ids = ['st-marys', 'st-josephs'];
      const results = await Promise.all(
        ids.map(id => api.getChurchImageMeta(id).then(r => [id, r.data]).catch(() => [id, { exists: false, updated_at: 0 }]))
      );
      setChurchImgs(Object.fromEntries(results));
      setChurchImgsLoaded(true);
    };
    loadImgs();
  }, []);

  const FALLBACK_CHURCH_IMAGE = {
    'st-marys': 'https://images.unsplash.com/photo-1511527661048-7fe73d85e9a4?crop=entropy&cs=srgb&fm=jpg&w=1200&q=85',
    'st-josephs': 'https://images.unsplash.com/photo-1465848059293-208e11dfea17?crop=entropy&cs=srgb&fm=jpg&w=1200&q=85',
  };

  const getChurchImageSrc = (churchId) => {
    // Never return the fallback URL until we know whether an admin-uploaded
    // photo exists — otherwise the stock image would flash first.
    if (!churchImgsLoaded) return null;
    const meta = churchImgs[churchId];
    if (meta?.exists) return api.churchImageUrl(churchId, meta.updated_at);
    return FALLBACK_CHURCH_IMAGE[churchId] || '';
  };

  const handleChurchFileChosen = async (churchId, file) => {
    if (!file) return;
    if (!/^image\/(jpeg|png|webp)$/i.test(file.type)) {
      toast({ title: 'Please choose a JPEG, PNG, or WebP image', variant: 'destructive' });
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast({ title: 'Image is larger than 10 MB', variant: 'destructive' });
      return;
    }
    setUploadingChurch(churchId);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await api.uploadChurchImage(churchId, fd);
      setChurchImgs(prev => ({ ...prev, [churchId]: { exists: true, updated_at: data.updated_at } }));
      toast({ title: 'Church photo updated' });
    } catch (e) {
      toast({ title: 'Upload failed', description: e?.response?.data?.detail || String(e), variant: 'destructive' });
    } finally {
      setUploadingChurch(null);
      // Clear input so the same filename can be re-chosen
      if (fileInputRefs.current[churchId]) fileInputRefs.current[churchId].value = '';
    }
  };

  // "What's Happening" on the home page shows only news items the admin
  // has explicitly starred as featured. No 3-item cap — curator picks any count.

  const handleSaveNews = async (values) => {
    if (editing === 'new') {
      const { data } = await api.createNews(values);
      setNews([data, ...news]);
      toast({ title: 'News item added' });
    } else {
      const { data } = await api.updateNews(editing.id, values);
      setNews(news.map(n => n.id === data.id ? data : n));
      setFeaturedNews(prev => prev.map(n => n.id === data.id ? { ...n, ...data, featured_on_home: n.featured_on_home, featured_home_order: n.featured_home_order } : n));
      toast({ title: 'News item updated' });
    }
  };

  const handleDeleteNews = async () => {
    await api.deleteNews(deleteTarget.id);
    setNews(news.filter(n => n.id !== deleteTarget.id));
    setFeaturedNews(prev => prev.filter(n => n.id !== deleteTarget.id));
    toast({ title: 'News item deleted' });
    setDeleteTarget(null);
  };

  const handleNewsPhotoUpload = async (n, file) => {
    if (!/^image\/(jpeg|png|webp)$/i.test(file.type)) {
      toast({ title: 'Please choose a JPEG, PNG, or WebP image', variant: 'destructive' });
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast({ title: 'Image is larger than 10 MB', variant: 'destructive' });
      return;
    }
    setUploadingNewsId(n.id);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await api.uploadNewsImage(n.id, fd);
      setNews(prev => prev.map(x => x.id === data.id ? data : x));
      setFeaturedNews(prev => prev.map(x => x.id === data.id ? { ...x, ...data } : x));
      toast({ title: 'Photo uploaded' });
    } catch (e) {
      toast({ title: 'Upload failed', description: e?.response?.data?.detail || String(e), variant: 'destructive' });
    } finally {
      setUploadingNewsId(null);
    }
  };

  const handleNewsPhotoRemove = async (n) => {
    setUploadingNewsId(n.id);
    try {
      const { data } = await api.deleteNewsImage(n.id);
      setNews(prev => prev.map(x => x.id === data.id ? data : x));
      setFeaturedNews(prev => prev.map(x => x.id === data.id ? { ...x, ...data } : x));
      toast({ title: 'Photo removed' });
    } catch (e) {
      toast({ title: 'Remove failed', description: e?.response?.data?.detail || String(e), variant: 'destructive' });
    } finally {
      setUploadingNewsId(null);
    }
  };

  // Unified merged list of home-featured items (news + sub-page tiles),
  // sorted by the globally-shared `featured_home_order` field so news and
  // tiles can interleave freely.
  const combinedFeatured = useMemo(() => {
    const items = [
      ...featuredNews.map(n => ({ kind: 'news', id: n.id, order: n.featured_home_order ?? 999999, data: n })),
      ...featuredTiles.map(t => ({ kind: 'tile', id: t.id, order: t.featured_home_order ?? 999999, data: t })),
    ];
    items.sort((a, b) => a.order - b.order);
    return items;
  }, [featuredNews, featuredTiles]);

  const moveFeatured = async (combinedItem, direction) => {
    const idx = combinedFeatured.findIndex(x => x.kind === combinedItem.kind && x.id === combinedItem.id);
    if (idx < 0) return;
    const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= combinedFeatured.length) return;
    const next = [...combinedFeatured];
    [next[idx], next[swapIdx]] = [next[swapIdx], next[idx]];
    // Optimistic: re-order both underlying arrays and re-assign order values 0..N-1
    const newsById = {};
    const tilesById = {};
    next.forEach((entry, i) => {
      const updated = { ...entry.data, featured_home_order: i };
      if (entry.kind === 'news') newsById[entry.id] = updated;
      else tilesById[entry.id] = updated;
    });
    const prevNews = featuredNews;
    const prevTiles = featuredTiles;
    setFeaturedNews(featuredNews.map(n => newsById[n.id] || n).sort((a, b) => (a.featured_home_order ?? 0) - (b.featured_home_order ?? 0)));
    setFeaturedTiles(featuredTiles.map(t => tilesById[t.id] || t).sort((a, b) => (a.featured_home_order ?? 0) - (b.featured_home_order ?? 0)));
    try {
      await api.reorderHomeFeatured(next.map(x => ({ type: x.kind, id: x.id })));
    } catch (e) {
      toast({ title: 'Reorder failed', description: e?.response?.data?.detail || String(e), variant: 'destructive' });
      setFeaturedNews(prevNews);
      setFeaturedTiles(prevTiles);
    }
  };

  const unfeatureNews = async (item) => {
    setFeaturedNews(prev => prev.filter(n => n.id !== item.id));
    setNews(prev => prev.map(n => n.id === item.id ? { ...n, featured_on_home: false } : n));
    try {
      await api.featureNewsOnHome(item.id, false);
      toast({ title: 'Removed from home page' });
    } catch (e) {
      setFeaturedNews(prev => [...prev, item]);
      setNews(prev => prev.map(n => n.id === item.id ? { ...n, featured_on_home: true } : n));
      toast({ title: 'Remove failed', description: e?.response?.data?.detail || String(e), variant: 'destructive' });
    }
  };

  const unfeatureTile = async (tile) => {
    // Optimistic remove
    setFeaturedTiles(prev => prev.filter(t => t.id !== tile.id));
    try {
      await api.featurePageTileOnHome(tile.id, false);
      toast({ title: 'Removed from home page' });
    } catch (e) {
      // Rollback
      setFeaturedTiles(prev => [...prev, tile]);
      toast({ title: 'Remove failed', description: e?.response?.data?.detail || String(e), variant: 'destructive' });
    }
  };

  return (
    <>
      <Hero />

      {/* Quick Action Grid */}
      <section className="relative -mt-14 md:-mt-20 z-10 max-w-7xl mx-auto px-6">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
          {QUICK_ACTIONS.map((qa) => {
            const Icon = iconMap[qa.icon];
            return (
              <Link
                key={qa.title}
                to={qa.path}
                className="group bg-white border border-[#e9e0cc] hover:border-[#3a2359] p-5 md:p-7 flex flex-col items-start gap-3 md:gap-4 shadow-sm hover:shadow-md hover:-translate-y-1"
              >
                <div className="w-11 h-11 md:w-12 md:h-12 rounded-full bg-[#f2ead6] flex items-center justify-center text-[#3a2359] group-hover:bg-[#3a2359] group-hover:text-[#f2ead6]">
                  <Icon size={20} strokeWidth={1.6} />
                </div>
                <h3 className="font-display text-xl md:text-2xl text-[#2a1a45] leading-tight">{qa.title}</h3>
                <p className="text-sm text-[#5a534a] leading-relaxed hidden md:block">{qa.description}</p>
                <span className="inline-flex items-center text-xs md:text-sm font-medium text-[#3a2359] group-hover:gap-2 gap-1">
                  Learn more <ChevronRight size={14} />
                </span>
              </Link>
            );
          })}
        </div>
      </section>

      {/* Welcome Section */}
      <section className="max-w-4xl mx-auto px-6 pt-24 pb-16 text-center">
        <Divider />
        <h1 className="font-display text-4xl md:text-6xl text-[#2a1a45] mt-4 mb-6 leading-tight">
          Welcome to All Saints Catholic Parish
        </h1>
        <EditableText
          contentKey="home_welcome_body"
          label="Home Welcome Message"
          rows={10}
          defaultValue={`All Saints is a Catholic parish serving Ladysmith and Chemainus on Vancouver Island. We are blessed with two churches — St. Mary's in Ladysmith and St. Joseph's in Chemainus — united as one faithful community of disciples.

Whether you're looking for Mass Times, new to the area, seeking a spiritual home, or just visiting — you are most welcome here. Please introduce yourself to our pastor after Mass, or register as a parishioner.`}
          paragraphClass="text-lg md:text-xl text-[#3f3a34] leading-relaxed font-light"
        />
      </section>

      {/* Two Churches - Side by Side */}
      <section className="bg-[#f2ead6]/40 py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-14">
            <p className="text-sm tracking-[0.2em] uppercase text-[#a68a5b] font-medium mb-3">Our Two Churches</p>
            <h2 className="font-display text-4xl md:text-5xl text-[#2a1a45]">One Parish, Two Communities</h2>
          </div>
          <div className="grid md:grid-cols-2 gap-8 lg:gap-12">
            {churches.map(c => (
              <div key={c.id} className="bg-white border border-[#e9e0cc] overflow-hidden group" data-testid={`church-card-${c.id}`}>
                <div className="relative h-64 md:h-72 overflow-hidden">
                  {getChurchImageSrc(c.id) ? (
                    <img src={getChurchImageSrc(c.id)} alt={c.name} className="w-full h-full object-cover group-hover:scale-105 duration-700" style={{transition: 'transform 700ms ease'}}/>
                  ) : (
                    <div className="w-full h-full bg-[#faf7f0]" data-testid={`church-image-loading-${c.id}`} />
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-[#2a1a45]/70 to-transparent" />
                  <div className="absolute bottom-5 left-6 text-white">
                    <p className="text-xs tracking-[0.2em] uppercase text-[#e6d4a8]">{c.community}</p>
                    <h3 className="font-display text-3xl md:text-4xl mt-1 text-white">{c.name}</h3>
                  </div>
                  {isAdmin && (
                    <>
                      <input
                        ref={el => { fileInputRefs.current[c.id] = el; }}
                        type="file"
                        accept="image/jpeg,image/png,image/webp"
                        className="hidden"
                        data-testid={`church-image-input-${c.id}`}
                        onChange={(e) => handleChurchFileChosen(c.id, e.target.files?.[0])}
                      />
                      <button
                        type="button"
                        onClick={() => fileInputRefs.current[c.id]?.click()}
                        disabled={uploadingChurch === c.id}
                        data-testid={`change-church-photo-${c.id}`}
                        className="absolute top-3 right-3 inline-flex items-center gap-2 px-3 py-1.5 bg-white/95 hover:bg-white text-[#3a2359] text-xs font-medium shadow disabled:opacity-70"
                      >
                        {uploadingChurch === c.id ? (
                          <><Loader2 size={14} className="animate-spin" /> Uploading…</>
                        ) : (
                          <><Camera size={14} /> Change photo</>
                        )}
                      </button>
                    </>
                  )}
                </div>
                <div className="p-6 md:p-8">
                  <div className="flex items-start gap-2 text-sm text-[#5a534a] mb-2">
                    <MapPin size={16} className="mt-0.5 flex-shrink-0 text-[#a68a5b]" />
                    <EditableInline
                      contentKey={`church_${c.id}_address`}
                      defaultValue={c.address}
                      label={`${c.name} address`}
                      placeholder="Street, City, Province Postal"
                    />
                  </div>
                  <div className="flex items-center gap-2 text-sm text-[#5a534a] mb-6">
                    <Phone size={16} className="flex-shrink-0 text-[#a68a5b]" />
                    <EditableInline
                      as="a"
                      href="tel:auto"
                      contentKey={`church_${c.id}_phone`}
                      defaultValue={c.phone}
                      label={`${c.name} phone`}
                      className="hover:text-[#3a2359]"
                      placeholder="(250) 555-0000"
                    />
                  </div>
                  <div className="border-t border-[#e9e0cc] pt-5">
                    <p className="text-xs tracking-[0.15em] uppercase text-[#a68a5b] font-medium mb-3">Weekly Schedule</p>
                    <ul className="space-y-2.5">
                      {c.schedule.slice(0, 4).map((s) => (
                        <li key={s.id} className="flex items-baseline justify-between gap-3 text-sm">
                          <span className="text-[#2a1a45]">
                            <span className="font-medium">{s.day}</span>
                            <span className="text-[#78716c]"> — {s.label}</span>
                          </span>
                          <span className="font-display text-lg text-[#3a2359] whitespace-nowrap">{s.time}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="text-center mt-10">
            <Link to="/mass-times" className="inline-flex items-center gap-2 px-6 py-3 bg-[#3a2359] text-white text-sm tracking-wide hover:bg-[#4d2f75]">
              View Full Mass Schedule <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* What's Happening — mirrors the top row on /parish-life/news */}
      <section className="max-w-7xl mx-auto px-6 py-20">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-10 gap-3">
          <div>
            <p className="text-sm tracking-[0.2em] uppercase text-[#a68a5b] font-medium mb-2">Parish News</p>
            <h2 className="font-display text-4xl md:text-5xl text-[#2a1a45]">What&rsquo;s Happening?</h2>
          </div>
          <div className="flex items-center gap-3">
            {isAdmin && <AddButton onClick={() => setEditing('new')} label="Add News" />}
            <Link to="/parish-life/news" className="text-sm text-[#3a2359] hover:text-[#5b3a8a] inline-flex items-center gap-1 font-medium">
              All news <ChevronRight size={16} />
            </Link>
          </div>
        </div>
        {combinedFeatured.length === 0 ? (
          <div className="text-center py-10">
            <p className="text-sm text-[#78716c]">No news featured yet.</p>
            {isAdmin && (
              <p className="mt-2 text-xs text-[#a68a5b] tracking-wide">
                Visit <Link to="/parish-life/news" className="text-[#3a2359] underline">News &amp; Resources</Link> or any ministry page and star items you'd like to appear here.
              </p>
            )}
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="home-happening-grid">
            {combinedFeatured.map((entry, i) => {
              const canMoveUp = i > 0;
              const canMoveDown = i < combinedFeatured.length - 1;
              if (entry.kind === 'news') {
                const n = entry.data;
                return (
                  <NewsCard
                    key={`news-${n.id}`}
                    news={n}
                    isAdmin={isAdmin}
                    uploading={uploadingNewsId === n.id}
                    onEdit={() => setEditing(n)}
                    onDelete={() => setDeleteTarget(n)}
                    onFileChosen={handleNewsPhotoUpload}
                    onRemoveImage={handleNewsPhotoRemove}
                    onMoveUp={() => moveFeatured(entry, 'up')}
                    onMoveDown={() => moveFeatured(entry, 'down')}
                    onToggleFeature={unfeatureNews}
                    canMoveUp={canMoveUp}
                    canMoveDown={canMoveDown}
                  />
                );
              }
              const t = entry.data;
              return (
                <TileCard
                  key={`tile-${t.id}`}
                  tile={t}
                  isAdmin={isAdmin}
                  uploading={false}
                  onToggleFeature={unfeatureTile}
                  onMoveUp={() => moveFeatured(entry, 'up')}
                  onMoveDown={() => moveFeatured(entry, 'down')}
                  canMoveUp={canMoveUp}
                  canMoveDown={canMoveDown}
                />
              );
            })}
          </div>
        )}
      </section>

      {/* Sacraments Preview */}
      <section className="bg-[#2a1a45] text-[#f2ead6] py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-14">
            <p className="text-sm tracking-[0.2em] uppercase text-[#e6d4a8] font-medium mb-3">The Seven Sacraments</p>
            <h2 className="font-display text-4xl md:text-5xl text-white">Encountering Christ</h2>
            <p className="max-w-2xl mx-auto mt-4 text-[#d5c9a5] leading-relaxed">
              The sacraments are the great signs and instruments of grace by which Christ acts in and through his Church.
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {SACRAMENTS.slice(0, 8).map(s => (
              <Link
                key={s.slug}
                to={`/sacraments/${s.slug}`}
                className="border border-[#4a3466] hover:border-[#e6d4a8] p-5 text-center hover:bg-[#3a2359]"
              >
                <h3 className="font-display text-xl md:text-2xl text-white leading-tight">{s.title}</h3>
                <span className="mt-3 inline-block text-xs text-[#d5c9a5] hover:text-white">Learn more →</span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Parish Life Preview */}
      <section className="max-w-7xl mx-auto px-6 py-20">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-10 gap-3">
          <div>
            <p className="text-sm tracking-[0.2em] uppercase text-[#a68a5b] font-medium mb-2">Get Involved</p>
            <h2 className="font-display text-4xl md:text-5xl text-[#2a1a45]">Parish Life</h2>
          </div>
          <Link to="/parish-life" className="text-sm text-[#3a2359] hover:text-[#5b3a8a] inline-flex items-center gap-1 font-medium">
            All parish life <ChevronRight size={16} />
          </Link>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-5">
          {PARISH_LIFE_TILES.map(t => {
            const Icon = t.icon;
            return (
              <Link
                key={t.path}
                to={t.path}
                data-testid={`home-parish-life-tile-${t.path.split('/').pop()}`}
                className="group bg-white border border-[#e9e0cc] hover:border-[#3a2359] hover:-translate-y-0.5 p-6 flex flex-col"
              >
                <div className="w-11 h-11 rounded-full bg-[#f2ead6] flex items-center justify-center text-[#3a2359] mb-4 group-hover:bg-[#3a2359] group-hover:text-[#e6d4a8]">
                  <Icon size={18} strokeWidth={1.6} />
                </div>
                <h3 className="font-display text-xl text-[#2a1a45] mb-2 leading-snug group-hover:text-[#3a2359]">{t.title}</h3>
                <p className="text-sm text-[#5a534a] leading-relaxed flex-1">{t.caption}</p>
                <span className="mt-4 inline-flex items-center text-xs font-medium text-[#3a2359] gap-1 group-hover:gap-2">
                  Explore <ChevronRight size={14} />
                </span>
              </Link>
            );
          })}
        </div>
      </section>

      {/* Mission Verse */}
      <section className="relative bg-[#f2ead6]/40 py-24">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <Divider />
          <p className="text-sm tracking-[0.2em] uppercase text-[#a68a5b] font-medium mt-4 mb-6">Our Mission</p>
          <blockquote className="font-display text-2xl md:text-3xl lg:text-4xl text-[#2a1a45] leading-snug italic font-light">
            &ldquo;{MISSION_VERSE.text}&rdquo;
          </blockquote>
          <cite className="block mt-6 text-sm text-[#5a534a] not-italic tracking-wide">— {MISSION_VERSE.citation}</cite>
        </div>
      </section>

      {/* News admin dialogs */}
      <EditDialog
        open={!!editing}
        title={editing === 'new' ? 'Add News Item' : 'Edit News Item'}
        fields={NEWS_FIELDS}
        initial={editing === 'new' ? { date: '', title: '', excerpt: '' } : (editing || {})}
        onClose={() => setEditing(null)}
        onSubmit={handleSaveNews}
      />
      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete this news item?"
        message={deleteTarget ? `“${deleteTarget.title}” will be permanently removed.` : ''}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={handleDeleteNews}
      />
    </>
  );
};

export default Home;
